import { modifyHistoryHashStatus, saveHashToLocalStorage } from '#imports';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { BigNumber } from 'bignumber.js';
import { ethers } from 'ethers';
import { defineStore } from 'pinia';
import { Expedition__factory, Interpreter__factory } from 'public/abi/evm/types';
import type { IUserModel } from '~/types/apiService';
import {
  type ExpeditionExtendedState,
  type ExpeditionLog,
  ExpeditionStatus,
  type ParsedCharacterData
} from '~/types/halloween/expedition';
import {
  type ExpectedRewardItem,
  HalloweenItem,
  type ItemDescription,
  type ItemWithQuantity
} from '~/types/halloween/items';
import type { HalloweenLocation } from '~/types/halloween/location';
import type {
  ExpeditionLocationResponse,
  ExpeditionLocationResponseRaw,
  UserExpeditionResponse
} from '~/types/halloween/responses';
import createRawStatefulLog from '~/utils/halloween/create-raw-stateful-log';
import halloweenItemRegistry from '~/utils/halloween/halloween-item-registry';
import parseExpeditionLogs from '~/utils/halloween/parse-expedition-logs';
import { CacheTime, RuntimeCache } from '~/utils/runtime-cache';

let expeditionsLastUpdatedAt = 0;
let expeditionUpdateInterval: any = null;
let lastExpeditionManualUpdate: number = 0;

export const useHalloweenStore = defineStore('halloween', () => {
  const tokenAddresses = ref(halloweenItemRegistry.getAllTokens(true).map((i) => i.address));
  const {
    fetchTokenBalances,
    getTokenBalance: external_getTokenBalance,
    tokenBalances
  } = useTokenBalances(tokenAddresses);
  const { blockchain } = useEnvs();
  const { getProvider, getSigner } = useProvider();
  const cache = new RuntimeCache();
  const { data: userData } = useNuxtData<IUserModel>('userData');
  const { sendContractMethod } = useSendContractMethod();
  const { apiUrl } = useEnvs();
  const { t } = useI18n();
  const store = useMainStore();

  const locationConfigurations = ref<Record<HalloweenLocation, ExpeditionLocationResponse | null>>({
    forest: null,
    cave: null
  });
  const locationExpectedRewardTokens = ref<Record<HalloweenLocation, ExpectedRewardItem[]>>({
    forest: [],
    cave: []
  });

  const statefulUserExpeditions = ref<ExpeditionExtendedState[]>([]);

  const availableCharacters = ref<ParsedCharacterData[]>([]);

  const runtimeSelectedLocation = ref<HalloweenLocation>('forest');

  const runtimeSelectedCharacters = ref<ParsedCharacterData[]>([]);

  const isFinalModalOpen = ref(false);

  const runtimeFinishedExpeditionIds = ref<number[]>([]);

  const runtimeCancelledExpeditionIds = ref<number[]>([]);

  const expeditionPlayedLogs = ref<Record<number, string[]>>({});

  const { address } = useWeb3ModalAccount();

  const isShowHalloweenLoginModal = ref(false);
  const isShowHalloweenLocationSelectModal = ref(false);
  const isShowExpeditionProgressModal = ref(false);
  const isShowHireCharactersModal = ref(false);
  const isShowExpeditionStartModal = ref(false);
  const isShowExpeditionStartedModal = ref(false);
  const isExpeditionCompletedModalOpen = ref(false);
  const isWitchWelcomeModalOpen = ref(false);
  const isAvailableExpeditionsModalOpen = ref(false);
  const isSuppressingCraftingModal = ref(false);
  const isWaitingForExpeditionStartTransaction = ref(false);
  const storeInitializationLog = ref<string[]>([]);
  const initializationKey = ref(Math.random().toString(36));

  const appendStoreInitializationLog = (log: string) => {
    if (storeInitializationLog.value.length > 30) storeInitializationLog.value = storeInitializationLog.value.slice(1);
    storeInitializationLog.value.push(`[${new Date().toISOString()}][${initializationKey.value}] ${log}`);
  };

  const initialize = async (initKey: string) => {
    const locations = ['forest', 'cave'] as const;

    if (import.meta.client) {
      (window as any).readHalloweenStoreLogs = () => {
        return storeInitializationLog.value;
      };
    }

    appendStoreInitializationLog(`Started initialization with key`);
    appendStoreInitializationLog(`Expedition contract address: ${blockchain.contracts.expedition}`);
    appendStoreInitializationLog(`Interpreter contract address: ${blockchain.contracts.interpreter}`);

    for (const location of locations) {
      try {
        locationConfigurations.value[location] = await getLocationConfig(location);
        locationExpectedRewardTokens.value[location] = await getExpeditionExpectedRewardTokens(location);
      } catch {
        appendStoreInitializationLog(`Failed to initialize location ${location}`);
        // pass
      }
    }

    if (initKey !== initializationKey.value) {
      appendStoreInitializationLog(`Rejected on check 1`);
      return;
    }
    const nextStatefulValue = await getStatefulUserExpeditions().catch(() => []);

    if (initKey !== initializationKey.value) {
      appendStoreInitializationLog(`Rejected on check 2`);
      return;
    }
    statefulUserExpeditions.value = nextStatefulValue;

    const nextCharactersList = await getAvailableCharacters().catch(() => {
      appendStoreInitializationLog(`Error during getAvailableCharacters`);
      return [];
    });

    if (initKey !== initializationKey.value) {
      appendStoreInitializationLog(`Rejected on check 3`);
      return;
    }
    availableCharacters.value = nextCharactersList;

    appendStoreInitializationLog(`Loaded`);
    if (expeditionUpdateInterval) clearInterval(expeditionUpdateInterval);

    callBalanceUpdate();
    expeditionUpdateInterval = setInterval(async () => {
      const now = Date.now();
      if (now - expeditionsLastUpdatedAt < 1000 * 10) return;

      appendStoreInitializationLog(`Updating expeditions within interval...`);

      statefulUserExpeditions.value = await getStatefulUserExpeditions().catch(() => []);
      availableCharacters.value = await getAvailableCharacters().catch(() => []);

      appendStoreInitializationLog(`Done updating expeditions within interval`);

      expeditionsLastUpdatedAt = Date.now();
    }, 1000 * 60);
  };

  onMounted(async () => {
    halloweenItemRegistry.onInit(() => {
      tokenAddresses.value = halloweenItemRegistry.getAllTokens(true).map((i) => i.address);
    });

    const nextKey = Math.random().toString(36);
    initializationKey.value = nextKey;
    await initialize(nextKey);
  });
  watch(address, async (newAddress) => {
    if (newAddress) {
      const nextKey = Math.random().toString(36);
      initializationKey.value = nextKey;
      await initialize(nextKey);
    }
  });

  const callBalanceUpdate = () => {
    void fetchTokenBalances()
      .catch((e: any) => {
        appendStoreInitializationLog(`Error during token balance update: ${e.message}`);
      })
      .then(() => {
        appendStoreInitializationLog(
          `Token balances: ${JSON.stringify(tokenBalances.value.map((b) => [b.tokenAddress, b.formattedBalance]))}`
        );
      });
  };

  const expeditionContract = Expedition__factory.connect(blockchain.contracts.expedition, getProvider());
  const interpreterContract = Interpreter__factory.connect(blockchain.contracts.interpreter, getProvider());

  const destroy = () => {
    const interval = setInterval(() => {
      callBalanceUpdate();
    }, 30_000);

    return () => {
      clearInterval(interval);
      clearInterval(expeditionUpdateInterval);
    };
  };

  onScopeDispose(destroy);

  const getTokenBalance = (token: HalloweenItem | ItemDescription) => {
    const itemData = halloweenItemRegistry.get(token);

    return new BigNumber(external_getTokenBalance(itemData.address)).dp(0).toFixed();
  };

  // Contract interaction methods begin: READ-ONLY

  const getExpeditionExpectedRewardTokens = async (location: HalloweenLocation) => {
    const hash = ethers.keccak256(ethers.toUtf8Bytes(location));

    const contractResponse = await cache.run(
      `getExpeditionExpectedRewardTokens-${location}`,
      () => expeditionContract.getLocationRewardTokensByTimestamp(hash, Math.round(Date.now() / 1000)),
      CacheTime.Day
    );

    const value: ExpectedRewardItem[] = [];
    contractResponse.rewardTokens.forEach((token) => {
      const itemData = halloweenItemRegistry.getByAddress(token.tokenAddress);
      if (!itemData || value.some((t) => t.token.address.toLowerCase() === token.tokenAddress.toLowerCase()))
        return null;

      value.push({
        token: itemData,
        minQuantity: new BigNumber(token.minAmount.toString()).dp(1).toNumber(),
        maxQuantity: new BigNumber(token.maxAmount.toString()).dp(1).toNumber()
      });
    });

    appendStoreInitializationLog(`Retrieved expedition tokens ${location}: ${JSON.stringify(value)}`);

    return value;
  };

  const getAvailableCharacters = async () => {
    const address = userData.value?.user.address;

    if (!address) return [];

    return cache.run(
      `getAvailableCharacters-${address.toLowerCase()}`,
      async () => {
        const characterHashes = await expeditionContract.getCharacters(address);

        return parseCharactersFromHashArray(characterHashes);
      },
      CacheTime.Minute
    );
  };

  const getExpeditionState = async (expeditionId: number) => {
    const [rawState, lootRaw, initialCharacterState, currentCharacterState] = await cache.run(
      `getExpeditionState-${expeditionId}`,
      async () => {
        const _rawState = await expeditionContract.getExpeditionState(expeditionId);
        const lootRaw = await expeditionContract.parseLoot(
          Object.values(_rawState.expeditionStates.at(-1)?.inventory || [])
        );

        const [initialCharacterState, currentCharacterState] = await Promise.all([
          parseCharactersFromHashArray(_rawState.expeditionStates.at(0)?.aliveCharacters || []),
          parseCharactersFromHashArray(_rawState.expeditionStates.at(-1)?.aliveCharacters || [])
        ]);

        return [_rawState, lootRaw, initialCharacterState, currentCharacterState] as const;
      },
      CacheTime.Minute
    );

    const startedAtTimestamp = parseInt(rawState.expeditionSlot.startTimestamp.toString()) * 1000;
    const duration = parseInt(rawState.expeditionSlot.duration.toString());
    const interruptedDuration = parseInt(rawState.expeditionSlot.interruptedDuration.toString());
    const totalDuration = Math.round(
      interruptedDuration > 0 ? interruptedDuration + interruptedDuration / 2 : duration + duration / 2
    );

    const endsAtTimestamp = startedAtTimestamp + totalDuration * 1000;

    const inventoryLootRaw: ItemWithQuantity[] = nonNullable(
      lootRaw[0].map((item, index) => {
        const lootQuantity = lootRaw[1][index];
        if (!lootQuantity || item === ethers.ZeroAddress) return;

        const itemData = halloweenItemRegistry.getByAddress(item);
        if (!itemData) return;

        return {
          ...itemData,
          quantity: new BigNumber(lootQuantity.toString()).shiftedBy(-18).dp(0).toNumber()
        };
      })
    );

    const inventoryLoot: ItemWithQuantity[] = [];
    inventoryLootRaw.forEach((item) => {
      const idx = inventoryLoot.findIndex((i) => i.address.toLowerCase() === item.address.toLowerCase());
      if (idx === -1) {
        inventoryLoot.push(item);
      } else {
        inventoryLoot[idx].quantity += item.quantity;
      }
    });

    const statefulCharacterData: ExpeditionExtendedState['characters'] = initialCharacterState.map((character) => {
      const currentStateOfCharacter = currentCharacterState.find((i) => i.characterIndex === character.characterIndex);

      if (!currentStateOfCharacter) {
        return {
          ...character,
          currentHealth: 0,
          alive: false
        };
      }

      return {
        ...character,
        currentHealth: currentStateOfCharacter.health,
        alive: currentStateOfCharacter.health > 0
      };
    });

    let expeditionLogs: ExpeditionLog[] = [
      {
        timestampMs: startedAtTimestamp,
        items: [],
        shouldPlayAnimation: false,
        text: [t('halloweenExpeditionLogStarted')],
        uniqueEventIndex: 'halloweenExpeditionLogStarted'
      },
      ...nonNullable(
        rawState.expeditionStates.map((state, index) => {
          const previousLog = rawState.expeditionStates[index - 1] || null;
          const _log = createRawStatefulLog(state, index, rawState.expeditionSlot.locationId);
          const preProcessedLog = parseExpeditionLogs(
            t,
            _log,
            previousLog ? createRawStatefulLog(previousLog, index - 1, rawState.expeditionSlot.locationId) : null
          );

          if (!preProcessedLog.eventTranslationKeys.length) return null;

          const itemsList: ItemWithQuantity[] = [];
          if (preProcessedLog.healthChange) {
            itemsList.push(
              halloweenItemRegistry.withQuantity(
                halloweenItemRegistry.get(HalloweenItem.Heart),
                preProcessedLog.healthChange
              )
            );
          }

          if (preProcessedLog.inventoryChange) {
            itemsList.push(preProcessedLog.inventoryChange);
          }

          return {
            _index: index,
            shouldPlayAnimation: false,
            timestampMs: parseInt(state.eventTime.toString()) * 1000,
            text: preProcessedLog.eventTranslationKeys,
            items: itemsList,
            uniqueEventIndex: _log.uniqueEventIndex
          };
        })
      )
    ].reverse();

    if (endsAtTimestamp < Date.now()) {
      expeditionLogs.unshift({
        timestampMs: endsAtTimestamp,
        items: [],
        text: [t('halloweenExpeditionLogEnded')],
        shouldPlayAnimation: false,
        uniqueEventIndex: 'halloweenExpeditionLogEnded'
      });
    }

    expeditionLogs = expeditionLogs.map((val, i) => {
      if (i === expeditionLogs.length - 1) return val;

      if (i === 0)
        return {
          ...val,
          shouldPlayAnimation: !(expeditionPlayedLogs.value[expeditionId] ?? []).some((i) => i === val.uniqueEventIndex)
        };

      return val;
    });

    const expeditionState: ExpeditionExtendedState = {
      expeditionId,
      startedAt: new Date(startedAtTimestamp),
      endsAt: new Date(endsAtTimestamp),
      durationSeconds: totalDuration,
      claimed: rawState.expeditionSlot.claimed,
      returningHome:
        Date.now() - startedAtTimestamp > duration * 1000 ||
        interruptedDuration > 0 ||
        runtimeCancelledExpeditionIds.value.includes(expeditionId),
      returnsAt: new Date(startedAtTimestamp + totalDuration * 1000),
      claimAvailable: parseInt(rawState.expeditionStates.at(-1)?.status?.toString() || '-1') === ExpeditionStatus.ENDED,
      inventory: inventoryLoot,
      characters: statefulCharacterData,
      logs: expeditionLogs,
      seasonPoints: await getExpeditionSeasonPoints(expeditionId),
      location:
        rawState.expeditionSlot.locationId === ethers.keccak256(ethers.toUtf8Bytes('forest')) ? 'forest' : 'cave'
    };

    return expeditionState;
  };

  const getStatefulUserExpeditions = async (location?: HalloweenLocation) => {
    const expeditionsRaw = await getUserExpeditions(location);

    return Promise.all(
      expeditionsRaw
        .filter((ex) => !runtimeFinishedExpeditionIds.value.includes(ex.expeditionId))
        .map((expedition) => getExpeditionState(expedition.expeditionId))
    );
  };

  const getUserExpeditions = async (location?: HalloweenLocation) => {
    const address = userData.value?.user.address;
    const locationHash = location ? ethers.keccak256(ethers.toUtf8Bytes(location)) : null;

    if (!address) throw new Error('User address is not defined');

    const result = await $fetch<UserExpeditionResponse[]>(`${apiUrl}/halloween/expeditions/user/${address}`, {
      query: {
        isActive: true
      }
    });

    if (!location) return result;
    return result.filter((e) => e.locationId === locationHash);
  };

  const getLocationConfig = async (location: HalloweenLocation) => {
    const locationHash = ethers.keccak256(ethers.toUtf8Bytes(location));

    const result = await $fetch<ExpeditionLocationResponseRaw>(`${apiUrl}/halloween/locations/${locationHash}`);

    const response: ExpeditionLocationResponse = {
      location,
      maxDuration: result.maxDuration,
      minDuration: result.minDuration,
      startTime: new Date(result.startTime),
      config: nonNullable(
        result.config.map((config) => {
          const tokenDetails = halloweenItemRegistry.getByAddress(config.tokenAddress);
          if (!tokenDetails) return null;

          return halloweenItemRegistry.withQuantity(tokenDetails, new BigNumber(config.amount).dp(1).toNumber());
        })
      )
    };

    return response;
  };

  // Contract interaction methods end: READ-ONLY

  // Contract interaction methods begin: WRITE

  const startExpedition = async (characters: ParsedCharacterData[], location: HalloweenLocation, timeHours: number) => {
    const locationHash = ethers.keccak256(ethers.toUtf8Bytes(location));
    const writeContract = Expedition__factory.connect(blockchain.contracts.expedition, await getSigner());

    const transaction = await sendContractMethod({
      contract: writeContract,
      methodName: 'startExpedition',
      args: [locationHash, characters.map((c) => c.characterIndex).sort(), timeHours * 3600]
    }).catch(() => {
      return null;
    });

    if (!transaction) return false;

    saveHashToLocalStorage(`${t('halloweenTransactionStartExpedition')} ${t(location)}`, transaction.hash);
    store.updateVersion();

    await transaction.wait(2);

    cache.invalidate((key) => key.startsWith('getExpeditionState-'));

    while (!statefulUserExpeditions.value.some((e) => e.location === location)) {
      statefulUserExpeditions.value = await getStatefulUserExpeditions();
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }

    modifyHistoryHashStatus(transaction.hash, 'Done');
    store.updateVersion();

    callBalanceUpdate();

    cache.invalidate((key) => key.startsWith('getAvailableCharacters-'));
    availableCharacters.value = await getAvailableCharacters();

    return true;
  };

  const interruptExpedition = async (expeditionId: number) => {
    const rawState = await cache.run(
      `getExpeditionRawState-${expeditionId}`,
      () => expeditionContract.getExpeditionState(expeditionId),
      CacheTime.Minute * 5
    );

    const writeContract = Expedition__factory.connect(blockchain.contracts.expedition, await getSigner());

    const lastStateStatus =
      parseInt(rawState.expeditionStates.at(-1)?.status?.toString() || '-1') !== ExpeditionStatus.STARTED;
    if (rawState.expeditionSlot.claimed || rawState.expeditionSlot.interruptedDuration > BigInt(0) || lastStateStatus)
      return false;

    cache.invalidate(`getExpeditionState-${expeditionId}`);
    cache.invalidate(`getExpeditionRawState-${expeditionId}`);

    const transaction = await sendContractMethod({
      contract: writeContract,
      methodName: 'interruptExpedition',
      args: [expeditionId]
    }).catch(() => {
      return null;
    });

    if (!transaction) return false;
    const locationName =
      rawState.expeditionSlot.locationId === ethers.keccak256(ethers.toUtf8Bytes('forest')) ? 'forest' : 'cave';

    saveHashToLocalStorage(`${t('halloweenTransactionInterruptExpedition')} ${locationName}`, transaction.hash);
    store.updateVersion();

    await transaction.wait(2);

    runtimeCancelledExpeditionIds.value = [...runtimeCancelledExpeditionIds.value, expeditionId];

    cache.invalidate((key) => key.startsWith('getExpeditionState-'));
    statefulUserExpeditions.value = await getStatefulUserExpeditions();

    modifyHistoryHashStatus(transaction.hash, 'Done');
    store.updateVersion();

    return true;
  };

  const claimExpeditionReward = async (expeditionId: number) => {
    const rawState = await cache.run(
      `getExpeditionRawState-${expeditionId}`,
      () => expeditionContract.getExpeditionState(expeditionId),
      CacheTime.Minute * 5
    );

    const signer = await getSigner();
    const writeContract = Expedition__factory.connect(blockchain.contracts.expedition, signer);
    const lastStateStatus =
      parseInt(rawState.expeditionStates.at(-1)?.status?.toString() || '-1') !== ExpeditionStatus.ENDED;

    if (lastStateStatus) {
      console.log('info lastStateStatus', lastStateStatus);
      return false;
    }

    const transaction = await sendContractMethod({
      contract: writeContract,
      methodName: 'claimExpedition',
      args: [expeditionId]
    }).catch(() => {
      return null;
    });

    if (!transaction) {
      console.log('info claimExpeditionReward', expeditionId);
      return false;
    }

    saveHashToLocalStorage(`${t('halloweenTransactionClaimExpedition')}`, transaction.hash);
    store.updateVersion();

    await transaction.wait(2);

    while (statefulUserExpeditions.value.every((e) => e.expeditionId !== expeditionId)) {
      statefulUserExpeditions.value = await getStatefulUserExpeditions();
      cache.invalidate((key) => key.startsWith('getExpeditionState-'));
      cache.invalidate((key) => key.startsWith('getExpeditionRawState-'));
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }

    cache.invalidate((key) => key.startsWith('getAvailableCharacters-'));
    availableCharacters.value = await getAvailableCharacters();

    statefulUserExpeditions.value = statefulUserExpeditions.value.filter((e) => e.expeditionId !== expeditionId);
    runtimeFinishedExpeditionIds.value = [...runtimeFinishedExpeditionIds.value, expeditionId];

    modifyHistoryHashStatus(transaction.hash, 'Done');
    store.updateVersion();

    delete expeditionPlayedLogs.value[expeditionId];

    callBalanceUpdate();

    return true;
  };

  // Contract interaction methods end: WRITE

  // Utility functions begin

  const getTotalExpeditionPrice = (
    selectedCharacters: ParsedCharacterData[],
    location: HalloweenLocation,
    timeHours: number
  ) => {
    const goldOre = selectedCharacters.reduce((acc, char) => acc + char.goldOrePrice, 0);
    const pearl = selectedCharacters.reduce((acc, char) => acc + char.pearlPrice, 0);
    const locationSpecificItem = location === 'forest' ? HalloweenItem.Axe : HalloweenItem.Torch;

    return {
      goldOre,
      pearl,
      locationItem: {
        item: halloweenItemRegistry.get(locationSpecificItem),
        quantity: timeHours
      }
    };
  };

  const parseCharactersFromHashArray = async (hashes: string[]) => {
    const parsedCharactersRaw = await interpreterContract.parseCharacters(Object.values(hashes)).catch(() => {
      return null;
    });

    if (!parsedCharactersRaw) return [];

    const parsedCharacters: ParsedCharacterData[] = [];
    parsedCharactersRaw.forEach((data, index) => {
      if (
        parsedCharacters.some(
          (c) => parseInt(c.characterType.toString()) - 1 === parseInt(data.characterType.toString())
        )
      ) {
        return;
      }

      parsedCharacters.push({
        available: hashes[index]?.slice(0, 3) === '0x0',
        goldOrePrice: new BigNumber(data.goldOrePrice.toString()).shiftedBy(-18).dp(1).toNumber(),
        pearlPrice: new BigNumber(data.pearlPrice.toString()).shiftedBy(-18).dp(0).toNumber(),
        attack: new BigNumber(data.attack.toString()).dp(0).toNumber(),
        defense: new BigNumber(data.defense.toString()).dp(0).toNumber(),
        health: new BigNumber(data.health.toString()).dp(0).toNumber(),
        characterType: parseInt(data.characterType.toString()) + 1,
        characterIndex: parseInt(data.characterIndex.toString())
      });
    });

    return parsedCharacters;
  };

  const handleExpeditionLocationSelect = (location: HalloweenLocation, callback?: () => any) => {
    if (!userData.value) {
      callback?.();

      isShowHalloweenLoginModal.value = true;
      return;
    }

    runtimeSelectedLocation.value = location;

    cache.invalidate((key) => key.startsWith('getExpeditionState-'));
    getStatefulUserExpeditions().then((expeditions) => {
      statefulUserExpeditions.value = expeditions;
      const currentLocationExpedition = expeditions.find((e) => e.location === location);
      if (currentLocationExpedition) {
        if (isExpeditionFinished(currentLocationExpedition.expeditionId)) {
          isExpeditionCompletedModalOpen.value = true;
        } else {
          isShowExpeditionProgressModal.value = true;
        }
      } else {
        isShowHalloweenLocationSelectModal.value = true;
      }
    });
  };

  const handleAutoModalTriggerAction = (userData: Ref<IUserModel | null>) => {
    if (!userData.value) {
      isShowHalloweenLoginModal.value = true;
      return;
    }

    const isForestActive = statefulUserExpeditions.value.some(
      (exp) => exp.location === 'forest' && (!exp.claimAvailable || isExpeditionFinished(exp.expeditionId))
    );
    const isCaveActive = statefulUserExpeditions.value.some(
      (exp) => exp.location === 'cave' && (!exp.claimAvailable || isExpeditionFinished(exp.expeditionId))
    );

    if (isForestActive && isCaveActive) {
      isAvailableExpeditionsModalOpen.value = true;
    } else {
      const longestLocation = statefulUserExpeditions.value.sort(
        (a, b) => b.endsAt.getTime() - a.endsAt.getTime()
      )?.[0];

      if (longestLocation) {
        runtimeSelectedLocation.value = longestLocation.location;
        if (isExpeditionFinished(longestLocation.expeditionId)) {
          isExpeditionCompletedModalOpen.value = true;
        } else {
          isShowExpeditionProgressModal.value = true;
        }
      } else {
        runtimeSelectedLocation.value = 'forest';
        isShowHalloweenLocationSelectModal.value = true;
      }
    }
  };

  const isExpeditionFinished = (expeditionId: number) => {
    const expedition = statefulUserExpeditions.value.find((exp) => exp.expeditionId === expeditionId);

    if (!expedition) return false;

    return expedition.characters.filter((c) => c.alive).length === 0
      ? true
      : expedition.returningHome
        ? expedition.returnsAt.getTime() < Date.now()
        : false;
  };

  const getExpeditionSeasonPoints = (expeditionId: number) => {
    return cache.run(
      `seasonPoints-${expeditionId}`,
      async () => {
        try {
          const res = await expeditionContract.getSeasonPointsForExpedition(expeditionId);
          return parseInt(res.toString());
        } catch {
          return 0;
        }
      },
      CacheTime.Minute * 5
    );
  };

  // Utility functions end

  return {
    getTokenBalance,

    getExpeditionExpectedRewardTokens,
    getAvailableCharacters,
    getExpeditionState,
    startExpedition,
    interruptExpedition,
    claimExpeditionReward,
    getTotalExpeditionPrice,
    getUserExpeditions,
    getStatefulUserExpeditions,
    getLocationConfig,
    handleExpeditionLocationSelect,
    handleAutoModalTriggerAction,
    isExpeditionFinished,
    callBalanceUpdate,

    resetRuntimeState: () => {
      //runtimeSelectedLocation.value = 'forest';
      runtimeSelectedCharacters.value = [];
    },

    updateUserExpeditions: async (ifOlderThanMs?: number) => {
      if (ifOlderThanMs && Date.now() - lastExpeditionManualUpdate < ifOlderThanMs) return;
      lastExpeditionManualUpdate = Date.now();

      cache.invalidate((key) => key.startsWith('getExpeditionState-'));
      statefulUserExpeditions.value = await getStatefulUserExpeditions();

      callBalanceUpdate();
    },

    isVirtuallyCancelled: (expeditionId: number) => runtimeCancelledExpeditionIds.value.includes(expeditionId),

    markTextAnimationAsPlayed: (expeditionId: number, log: ExpeditionLog) => {
      expeditionPlayedLogs.value[expeditionId] = [
        ...(expeditionPlayedLogs.value[expeditionId] || []),
        log.uniqueEventIndex
      ];
    },

    locationConfigurations,
    locationExpectedRewardTokens,
    statefulUserExpeditions,
    runtimeSelectedLocation,
    availableCharacters,
    runtimeSelectedCharacters,
    isFinalModalOpen,

    isShowExpeditionProgressModal,
    isShowHireCharactersModal,
    isShowExpeditionStartModal,
    isShowExpeditionStartedModal,
    isExpeditionCompletedModalOpen,
    isShowHalloweenLoginModal,
    isShowHalloweenLocationSelectModal,
    isWitchWelcomeModalOpen,
    isAvailableExpeditionsModalOpen,
    isWaitingForExpeditionStartTransaction,
    isSuppressingCraftingModal,
    storeInitializationLog,
    tokenBalances
  };
});
